import {
  compose,
  concat,
  isEmpty,
  isNil,
  path,
  reduce,
  reject,
  values,
} from 'ramda'

import {commandJson} from 'components/proposals/Part/shared/changePayload'
import type {State} from '../types'

const domainJson = (parentDeleted) => commandJson('Domain', parentDeleted)
const accountabilityJson = (parentDeleted) => commandJson('Accountability', parentDeleted)
const parentRoleDatabaseId = path(['role', 'circle', 'supportedRole', 'databaseId'])
const concatAll = compose(reject(isNil), reduce(concat, []))

const moveRoleDown = (fromRoleDatabaseId) => (
  ({isSelected, databaseId: roleDatabaseId}) => (
    isSelected
      ? {type: 'MoveRole', roleDatabaseId, fromRoleDatabaseId}
      : null
  )
)

const moveRoleUp = (fromRoleDatabaseId, toRoleDatabaseId) => (
  ({isSelected, databaseId: roleDatabaseId}) => (
    isSelected
      ? {type: 'MoveRole', roleDatabaseId, fromRoleDatabaseId, toRoleDatabaseId}
      : null
  )
)

const movePolicyDown = (fromRoleDatabaseId) => (
  ({isSelected, databaseId: policyDatabaseId}) => (
    isSelected
      ? {type: 'MovePolicy', policyDatabaseId, fromRoleDatabaseId}
      : null
  )
)

const movePolicyUp = (fromRoleDatabaseId, toRoleDatabaseId) => (
  ({isSelected, databaseId: policyDatabaseId}) => (
    isSelected
      ? {type: 'MovePolicy', policyDatabaseId, fromRoleDatabaseId, toRoleDatabaseId}
      : null
  )
)

class CommandPayload {
  constructor(change, state: State) {
    const {command} = change
    this.command = JSON.parse(command)
    this.parentRoleDatabaseId = parentRoleDatabaseId(change)
    this.state = state
    this.databaseId = this.command.databaseId
  }

  get children() {
    const domains = this.state.domains.map(domainJson(this.destroyEntirely))

    const accountabilities = this.state.accountabilities
      .map(accountabilityJson(this.destroyEntirely))

    const moveItems = this.moveItems

    return domains
      .concat(accountabilities)
      .concat(moveItems)
      .filter(Boolean)
  }

  get moveRoleDown() {
    return moveRoleDown(this.databaseId)
  }

  get moveRoleUp() {
    return moveRoleUp(this.databaseId, this.parentRoleDatabaseId)
  }

  get movePolicyDown() {
    return movePolicyDown(this.databaseId)
  }

  get movePolicyUp() {
    return movePolicyUp(this.databaseId, this.parentRoleDatabaseId)
  }

  get rolesDown() {
    return values(this.state.rolesToMoveDown).map(this.moveRoleDown)
  }

  get rolesUp() {
    return values(this.state.rolesToMoveUp).map(this.moveRoleUp)
  }

  get policiesDown() {
    return values(this.state.policiesToMoveDown).map(this.movePolicyDown)
  }

  get policiesUp() {
    return values(this.state.policiesToMoveUp).map(this.movePolicyUp)
  }

  get destroyEntirely(): boolean {
    return !!(this.state.destroyRole || this.state.destroyCircle)
      && !this.state.destroyCircleKeepRole
  }

  get moveItemsUp(): boolean {
    return this.state.isOnV4 && !!(this.state.destroyCircleKeepRole || this.state.destroyCircle)
  }

  get moveItems() {
    const items = concatAll([
      this.rolesDown,
      this.policiesDown,
      this.rolesUp,
      this.policiesUp,
    ])

    if (isEmpty(items))
      return null

    if (this.moveItemsUp || !!this.state.restructureCircle) {
      return [
        {
          type: 'MoveItems',
          roleDatabaseId: this.parentRoleDatabaseId,
          childRoleDatabaseId: this.databaseId,
          children: items,
        },
      ]
    }

    return [
      {
        type: 'MoveItems',
        roleDatabaseId: this.databaseId,
        children: items,
      },
    ]
  }

  get body() {
    return {
      ...this.command,
      children: this.children,
      destroy: !!this.state.destroyRole,
      destroyCircle: !!(this.state.destroyCircle || this.state.destroyCircleKeepRole),
      destroyCircleKeepRole: !!this.state.destroyCircleKeepRole,
      expand: !!this.state.expandRole,
      restructure: !!this.state.restructureCircle,
      name: this.state.name.nextValue,
      purpose: this.state.purpose.nextValue,
      roleTemplateId: this.state.roleTemplateId,
    }
  }

  call() {
    return {
      command: JSON.stringify(this.body),
    }
  }
}

export default (change, state) => (new CommandPayload(change, state)).call()
